import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/layout/PageHeader'
import { WeekGrid } from '@/components/opening-hours/WeekGrid'
import { OpeningHourForm } from '@/components/opening-hours/OpeningHourForm'
import { Skeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { useCopyOpeningHoursFromSchedule, useOpeningHours } from '@/lib/queries/useOpeningHours'
import {
  openingHourMatchesSchedule,
  type OpeningHour,
  type OpeningHourSchedule,
} from '@/lib/types/openingHour'

const SCHEDULES: Array<{
  key: OpeningHourSchedule
  title: string
  description: string
}> = [
  { key: 'booking', title: 'Bookings', description: 'Dining reservations and table availability' },
  { key: 'order', title: 'Orders', description: 'Collection and takeaway ordering hours' },
  { key: 'delivery', title: 'Delivery', description: 'Delivery ordering and dispatch hours' },
]

export default function OpeningHoursPage() {
  const { t } = useTranslation()
  const { data: resp, isLoading } = useOpeningHours()
  const hours = resp?.data ?? []
  const copyHours = useCopyOpeningHoursFromSchedule()
  const [showForm, setShowForm] = useState(false)
  const [editHour, setEditHour] = useState<OpeningHour | null>(null)
  const [defaultDay, setDefaultDay] = useState(1)
  const [defaultSpecial, setDefaultSpecial] = useState(false)
  const [activeSchedule, setActiveSchedule] = useState<OpeningHourSchedule>('booking')

  const visibleHours = useMemo(
    () => hours.filter((hour) => openingHourMatchesSchedule(hour, activeSchedule)),
    [activeSchedule, hours],
  )
  const activeScheduleMeta = SCHEDULES.find((schedule) => schedule.key === activeSchedule) ?? SCHEDULES[0]

  const openForDay = (day: number) => {
    setDefaultDay(day)
    setDefaultSpecial(false)
    setEditHour(null)
    setShowForm(true)
  }

  const openForSpecial = () => {
    setDefaultSpecial(true)
    setEditHour(null)
    setShowForm(true)
  }

  const openForEdit = (h: OpeningHour) => {
    setEditHour(h)
    setDefaultSpecial(h.special)
    setShowForm(true)
  }

  const handleClose = () => {
    setShowForm(false)
    setEditHour(null)
    setDefaultSpecial(false)
  }

  if (isLoading) {
    return (
      <div>
        <PageHeader title={t('openingHours.title')} description={t('openingHours.description')} />
        <div className="grid grid-cols-7 gap-3">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} variant="rectangular" height={200} />)}
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title={t('openingHours.title')} description={t('openingHours.description')} />
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {SCHEDULES.map((schedule) => {
            const active = schedule.key === activeSchedule
            return (
              <button
                key={schedule.key}
                type="button"
                onClick={() => setActiveSchedule(schedule.key)}
                className={`rounded-[var(--radius-md)] border px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border-[var(--color-accent-amber)] bg-[var(--color-accent-amber-dim)] text-[var(--color-accent-amber)]'
                    : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-default)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                {schedule.title}
              </button>
            )
          })}
        </div>
        <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{activeScheduleMeta.title}</h2>
            <p className="text-sm text-[var(--color-text-muted)]">{activeScheduleMeta.description}</p>
          </div>
          {activeSchedule !== 'booking' && (
            <Button
              variant="secondary"
              onClick={() =>
                copyHours.mutate({
                  hours,
                  sourceSchedule: 'booking',
                  targetSchedule: activeSchedule,
                })
              }
              loading={copyHours.isPending}
            >
              Make Like Bookings
            </Button>
          )}
        </div>
      </div>
      <WeekGrid
        hours={visibleHours}
        onSlotClick={openForDay}
        onEditHour={openForEdit}
        onAddSpecial={openForSpecial}
      />
      <OpeningHourForm
        open={showForm}
        onClose={handleClose}
        hour={editHour}
        defaultDay={defaultDay}
        defaultSpecial={defaultSpecial}
        defaultMode={activeSchedule}
      />
    </div>
  )
}
